import axios from 'axios';
import type { UseFormRegisterReturn } from 'react-hook-form';

/**
 * The second STEP of a sign-in, not a second page (decision 18).
 *
 * The five password login pages are the same form five times over, so the
 * shared part lives here: how to recognise the server asking for a code, and
 * the field it asks with. A page that copied this inline would be the sixth
 * near-identical copy, and the one that quietly drifts.
 *
 * The backend answers the first POST with `TwoFactorRequired`, which
 * `custom_exception_handler` renders as
 * `{ success: false, error: { code: 'two_factor_required', message } }`.
 * Branching on `code` rather than on the English message is the whole reason
 * that exception carries its own `default_code`.
 */
export const TWO_FACTOR_REQUIRED_CODE = 'two_factor_required';

export function isTwoFactorRequired(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const data = error.response?.data as Record<string, unknown> | undefined;
  const errObj = data?.error as Record<string, unknown> | undefined;
  return errObj?.code === TWO_FACTOR_REQUIRED_CODE;
}

interface TwoFactorCodeFieldProps {
  registration: UseFormRegisterReturn;
  /** The server's own words for why the last code was refused, if any. */
  message?: string;
  /** Tailwind ring/accent colour, so each portal's form stays its own colour. */
  accentClassName?: string;
}

export function TwoFactorCodeField({
  registration,
  message,
  accentClassName = 'text-primary-600',
}: TwoFactorCodeFieldProps) {
  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-500/40 dark:bg-amber-500/10"
      data-testid="two-factor-step"
    >
      <label htmlFor="two_factor_code" className="block text-sm font-medium text-gray-900 dark:text-gray-100">
        Two-factor code
      </label>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
        {message || 'Enter the 6-digit code from your authenticator app, or one of your backup codes.'}
      </p>
      <input
        {...registration}
        id="two_factor_code"
        type="text"
        inputMode="text"
        autoComplete="one-time-code"
        autoFocus
        className="input mt-3 tracking-widest"
        placeholder="123456"
      />
      <p className={`mt-2 text-xs ${accentClassName}`}>
        Lost your authenticator? Use a backup code, or ask an administrator to reset it.
      </p>
    </div>
  );
}
